import { useNoPaste } from '../lib/noPaste';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { burst } from '../lib/confetti';
import { buildLesson, check, type GrammarConfig, type Question } from '../lib/grammar/topics';
import { lang as langOf } from '../lib/languages';
import { speak } from './TranslatableText';
import type { LessonSave } from '../lib/lessonSaves';

// ─────────────────────────────────────────────────────────────────────────────
// A grammar lesson, Duolingo-style.
//
//   • 3 hearts. A wrong answer costs one; at zero the lesson is lost (and does
//     not count) — "try again" builds a fresh one.
//   • A missed question comes back at the end, so a lesson always finishes on
//     everything answered right once.
//   • Typed answers that are right except for an accent count, with a note —
//     the habit to build is accents, not losing a heart over one.
//   • Score = questions right on the FIRST try / questions in the lesson.
//
// Everything is generated in the browser from the teacher's settings
// (lib/grammar/topics.ts) — no network until the result is saved.
// ─────────────────────────────────────────────────────────────────────────────

const HEARTS = 3;

export type LessonResult = { correct: number; total: number; seconds: number; xp: number };

type Props = {
  config?: GrammarConfig;
  /** Difficulty 1–5 for this lesson (see LEVELS). */
  level?: number;
  /** A ready-made lesson instead of one built from `config` (a story lesson, lib/storyLesson.ts). */
  questions?: Question[];
  /** Hearts to start with; null = no hearts (a story lesson can't be lost). */
  hearts?: number | null;
  /** A missed question comes back this many questions later (default: at the end). It must be answered right to finish. */
  retryAfter?: number;
  /** Where a lesson left part-way got to (lib/lessonSaves.ts): it picks up from there. Read once, when the lesson opens. */
  saved?: LessonSave | null;
  /** Called after every answer with the progress so far (null when it's over), to keep for next time. */
  onSave?: (s: LessonSave | null) => void;
  /** Put the questions themselves in the save (grammar: they're made at random). Never for story lessons. */
  keepQuestions?: boolean;
  /** Under the prompt: a 🔊 button that reads it aloud in this language, for prompts in the class language. */
  speakPrompt?: (q: Question) => string | null;
  title: string;
  /** The class's language: the typing keys and the praise come from it. */
  language: string;
  /** Save the result. Throwing shows the message and offers a retry. */
  onComplete: (r: LessonResult) => Promise<void>;
  onExit: () => void;
};

// ── Sound: two tiny Web Audio cues, no files to load ─────────────────────────
let ctx: AudioContext | null = null;
function tone(freqs: number[], dur = 0.12, type: OscillatorType = 'sine', gap = 0.09) {
  try {
    ctx ??= new AudioContext();
    freqs.forEach((f, i) => {
      const o = ctx!.createOscillator();
      const g = ctx!.createGain();
      o.type = type;
      o.frequency.value = f;
      const t = ctx!.currentTime + i * gap;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(g).connect(ctx!.destination);
      o.start(t);
      o.stop(t + dur + 0.02);
    });
  } catch { /* no audio: fine */ }
}
const MUTE_KEY = 'grammar.muted';
const isMuted = () => { try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; } };

type Feedback = { result: 'right' | 'accent' | 'wrong'; answer: string };


export default function GrammarLesson({ config, level, questions, hearts: startHearts = HEARTS, retryAfter, saved: savedProp, onSave, keepQuestions, speakPrompt, title, language, onComplete, onExit }: Props) {
  const noHearts = startHearts == null;
  const L = langOf(language);
  const ACCENTS = L.keys;
  const PRAISE = L.praise;
  const [seed, setSeed] = useState(0);
  // Picking up a lesson left part-way: only what the lesson opened with (later saves are ours), and not after a restart.
  const [opened] = useState(() => savedProp ?? null);
  const saved = seed === 0 ? opened : null;
  const initial = useMemo(
    () => questions ?? (saved?.questions?.length ? saved.questions : buildLesson(config!, level ?? 3)),
    [questions, config, level, seed], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const startQueue = useMemo(() => {
    if (!saved) return initial;
    const done = new Set(saved.done);
    const byKey = new Map(initial.map(q => [q.key, q]));
    // What was left, in the order it was left in; then anything new (a story rebuilt slightly differently).
    const left = (saved.queue ?? []).map(k => byKey.get(k)).filter((q): q is Question => !!q && !done.has(q.key));
    for (const q of initial) if (!done.has(q.key) && !left.includes(q)) left.push(q);
    return left.length ? left : initial;
  }, [initial, saved]);
  const [queue, setQueue] = useState<Question[]>(startQueue);
  const [done, setDone] = useState(() => (saved && startQueue !== initial ? saved.done.length : 0));
  const doneKeys = useRef<string[]>(saved && startQueue !== initial ? saved.done : []);
  // Hearts carry over a resume, so leaving and coming back doesn't refill them.
  const [hearts, setHearts] = useState(() => (startHearts == null ? Infinity : saved && startQueue !== initial && typeof saved.hearts === 'number' ? saved.hearts : startHearts));
  const [firstTry, setFirstTry] = useState<Set<string>>(() => new Set(saved && startQueue !== initial ? saved.first : []));
  const [missed, setMissed] = useState<Set<string>>(() => new Set(saved && startQueue !== initial ? saved.missed : []));
  const [answer, setAnswer] = useState('');
  const noPaste = useNoPaste();
  /** Build the sentence: indices into q.tiles, in the order tapped. */
  const [picked, setPicked] = useState<number[]>([]);
  /** Match: for each form (in q.pairs order), the chip (index into q.tiles) dropped on it. */
  const [placed, setPlacedState] = useState<(number | null)[]>([]);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [streak, setStreak] = useState(0);
  const [muted, setMuted] = useState(isMuted);
  const [phase, setPhase] = useState<'play' | 'saving' | 'won' | 'lost'>('play');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [result, setResult] = useState<LessonResult | null>(null);
  const started = useRef(Date.now());
  const inputRef = useRef<HTMLInputElement>(null);

  // Restart from scratch (new questions) — after a loss, or "practise again".
  const restart = () => {
    onSave?.(null);
    doneKeys.current = [];
    setSeed(s => s + 1);
    setHearts(startHearts ?? Infinity); setDone(0); setFirstTry(new Set()); setMissed(new Set());
    setAnswer(''); setPicked([]); setPlacedState([]); setFeedback(null); setStreak(0); setPhase('play'); setSaveError(null); setResult(null);
    started.current = Date.now();
  };
  useEffect(() => { setQueue(startQueue); }, [startQueue]);

  const total = initial.length;
  const q = queue[0];

  useEffect(() => { if (q?.kind === 'type' && !feedback) inputRef.current?.focus(); }, [q, feedback]);

  const finish = useCallback(async (finalFirstTry: Set<string>) => {
    const seconds = Math.round((Date.now() - started.current) / 1000);
    const correct = finalFirstTry.size;
    const r: LessonResult = { correct, total, seconds, xp: 10 + correct * 2 + (correct === total ? 5 : 0) };
    setResult(r);
    setPhase('saving');
    try {
      await onComplete(r);
      onSave?.(null);
      setPhase('won');
      burst();
      if (!muted) tone([523, 659, 784, 1047], 0.18, 'triangle', 0.11);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not save your lesson.');
      setPhase('won');
    }
  }, [onComplete, total, muted]);

  const submit = () => {
    if (!q || feedback) return;
    const given = answer.trim();
    if (!given) return;
    const r = q.kind === 'match' ? matchResult(q, placed) : check(q, given);
    const ok = r !== 'wrong';
    setFeedback({ result: r, answer: q.answers[0] });
    if (ok) {
      setStreak(s => s + 1);
      if (!missed.has(q.key)) setFirstTry(prev => new Set(prev).add(q.key));
      if (!muted) tone([660, 990]);
    } else {
      setStreak(0);
      setMissed(prev => new Set(prev).add(q.key));
      if (!noHearts) setHearts(h => h - 1);
      if (!muted) tone([220, 160], 0.22, 'square', 0.12);
    }
  };

  const next = () => {
    if (!feedback || !q) return;
    const ok = feedback.result !== 'wrong';
    if (!ok && hearts <= 0) {
      onSave?.(null);   // a lost lesson starts over
      setPhase('lost'); return;
    }
    const rest = queue.slice(1);
    // A miss comes back: a few questions later (retryAfter), or at the end.
    const at = retryAfter == null ? rest.length : Math.min(retryAfter, rest.length);
    const nextQueue = ok ? rest : [...rest.slice(0, at), q, ...rest.slice(at)];
    if (ok) {
      setDone(d => d + 1);
      doneKeys.current = [...doneKeys.current, q.key];
    }
    // Keep it for next time (the last answer is in: a finished lesson is saved by onComplete instead).
    if (onSave && nextQueue.length) {
      onSave({
        done: doneKeys.current, first: [...firstTry], missed: [...missed], queue: nextQueue.map(x => x.key),
        ...(noHearts ? {} : { hearts }), ...(keepQuestions ? { questions: initial } : {}), at: Date.now(),
      });
    }
    setAnswer('');
    setPicked([]);
    setPlacedState([]);
    setFeedback(null);
    if (nextQueue.length === 0) { void finish(firstTry); return; }
    setQueue(nextQueue);
  };

  // Keys: Enter checks / continues; 1–4 picks an option.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (phase !== 'play') return;
      // Typing somewhere else on top of the lesson (the 🐞 bug report): not for the lesson.
      const el = e.target as HTMLElement | null;
      if (el?.closest?.('textarea, input, select') && !el.closest('.lesson')) return;
      if (e.key === 'Enter') { e.preventDefault(); if (feedback) next(); else submit(); return; }
      if (!feedback && q?.kind === 'order' && e.key === 'Backspace' && picked.length) { setTiles(picked.slice(0, -1)); return; }
      if (!feedback && q?.kind === 'choice' && /^[1-9]$/.test(e.key)) {
        const opt = q.options?.[Number(e.key) - 1];
        if (opt) setAnswer(opt);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const setPlaced = (next: (number | null)[]) => {
    setPlacedState(next);
    const all = !!q?.pairs && q.pairs.every((_, k) => next[k] != null);
    setAnswer(all ? 'placed' : '');
  };

  const setTiles = (next: number[]) => {
    setPicked(next);
    setAnswer(next.map(i => q?.tiles?.[i] ?? '').join(' '));
  };

  const toggleMute = () => {
    const m = !muted;
    setMuted(m);
    try { localStorage.setItem(MUTE_KEY, m ? '1' : '0'); } catch { /* fine */ }
  };

  const exit = () => {
    if (phase === 'play' && done > 0 && !onSave && !window.confirm('Quit this lesson? Your progress in it will be lost.')) return;
    onExit();
  };

  // ── Screens ────────────────────────────────────────────────────────────────
  if (total === 0) {
    return (
      <div className="lesson">
        <div className="lesson-end">
          <p>This grammar assignment has no questions yet. Ask your teacher to check its settings.</p>
          <button className="primary" onClick={onExit}>Back</button>
        </div>
      </div>
    );
  }

  if (phase === 'lost') {
    return (
      <div className="lesson">
        <div className="lesson-end">
          <div className="big-emoji">💔</div>
          <h2>Out of hearts</h2>
          <p className="muted">That lesson doesn't count, but every mistake is practice. Try a fresh one!</p>
          <button className="primary big" onClick={restart}>Try again</button>
          <button onClick={onExit}>Not now</button>
        </div>
      </div>
    );
  }

  if ((phase === 'won' || phase === 'saving') && result) {
    const pct = Math.round((result.correct / result.total) * 100);
    return (
      <div className="lesson">
        <div className="lesson-end">
          <div className="big-emoji">{pct === 100 ? '🏆' : pct >= 80 ? '🎉' : '💪'}</div>
          <h2>{phase === 'saving' ? 'Saving…' : 'Lesson complete!'}</h2>
          <div className="end-stats">
            <div className="end-stat xp"><span className="k">Total XP</span><span className="n">⚡ {result.xp}</span></div>
            <div className="end-stat acc"><span className="k">Accuracy</span><span className="n">🎯 {pct}%</span></div>
            <div className="end-stat time"><span className="k">Time</span><span className="n">⏱ {Math.floor(result.seconds / 60)}:{String(result.seconds % 60).padStart(2, '0')}</span></div>
          </div>
          {saveError && (
            <div className="notice bad small">
              {saveError}
              <div style={{ marginTop: 8 }}><button className="small" onClick={() => void finish(firstTry)}>Try saving again</button></div>
            </div>
          )}
          <button className="primary big" disabled={phase === 'saving'} onClick={onExit}>Continue</button>
          <button disabled={phase === 'saving'} onClick={restart}>Practise again</button>
        </div>
      </div>
    );
  }

  if (!q) return null;

  const progress = (done / total) * 100;
  const [before, after] = q.prompt.includes('___') ? q.prompt.split('___') : [q.prompt, ''];
  const hasBlank = q.prompt.includes('___');
  const right = feedback && feedback.result !== 'wrong';

  return (
    <div className="lesson" role="dialog" aria-label={`${title} lesson`}>
      <div className="lesson-top">
        <button className="icon lesson-x" onClick={exit} aria-label="Quit lesson">✕</button>
        <div className="lesson-progress"><i style={{ width: `${progress}%` }} /></div>
        {noHearts ? <span className="hearts small" aria-label={`${done} of ${total} done`}>{done}/{total}</span>
          : <span className="hearts" aria-label={`${hearts} hearts`}>❤️ {hearts}</span>}
        <button className="icon" onClick={toggleMute} aria-label={muted ? 'Sound on' : 'Sound off'}>{muted ? '🔇' : '🔊'}</button>
      </div>

      <div className="lesson-body">
        {streak >= 3 && !feedback && <div className="combo">🔥 {streak} in a row!</div>}
        {missed.has(q.key) && !feedback && <div className="again-tag">↺ Let's try this one again</div>}
        <div className="lesson-instruction">{q.instruction}</div>
        <div className="lesson-prompt" dir="auto">
          {before}
          {hasBlank && (
            <span className={`blank${answer ? ' filled' : ''}`}>{q.kind === 'choice' && answer ? answer : ' '}</span>
          )}
          {after}
        </div>
        {q.hint && <div className="lesson-hint">{q.hint}</div>}
        {(() => { const say = speakPrompt?.(q); return say ? <button type="button" className="small lesson-say" onClick={() => speak(say, language)}>🔊 Listen</button> : null; })()}

        {q.kind === 'match' ? (
          <MatchBoard q={q} placed={placed} setPlaced={setPlaced} locked={!!feedback} />
        ) : q.kind === 'order' ? (
          <div className="tile-game">
            <div className={`tile-line${feedback ? (right ? ' good' : ' bad') : ''}`} dir="auto" aria-label="Your sentence">
              {picked.length === 0 && <span className="tile-placeholder">Tap the words below</span>}
              {picked.map((ti, k) => (
                <button key={`${ti}-${k}`} className="tile" disabled={!!feedback}
                  onClick={() => setTiles(picked.filter((_, j) => j !== k))}>{q.tiles![ti]}</button>
              ))}
            </div>
            <div className="tile-bank" dir="auto">
              {q.tiles!.map((t, i) => (
                <button key={i} className={`tile${picked.includes(i) ? ' used' : ''}`}
                  disabled={!!feedback || picked.includes(i)} aria-hidden={picked.includes(i)}
                  onClick={() => setTiles([...picked, i])}>{t}</button>
              ))}
            </div>
          </div>
        ) : q.kind === 'choice' ? (
          <div className="options">
            {q.options!.map((o, i) => (
              <button
                key={o}
                className={`option${answer === o ? ' picked' : ''}${feedback && o === q.answers[0] ? ' correct' : ''}${feedback && answer === o && !right ? ' wrong' : ''}`}
                onClick={() => !feedback && setAnswer(o)}
                disabled={!!feedback}
              >
                <span className="option-key">{i + 1}</span>{o}
              </button>
            ))}
          </div>
        ) : (
          <div className="type-answer">
            <input
              dir="auto"
              ref={inputRef}
              value={answer}
              onChange={e => setAnswer(e.target.value)}
              disabled={!!feedback}
              placeholder="Type your answer"
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              {...noPaste.props}
            />
            {noPaste.warned && <div className="no-paste" role="status">✋ No pasting: type it yourself!</div>}
            <div className="accent-keys" hidden={ACCENTS.length === 0}>
              {ACCENTS.map(c => (
                <button key={c} type="button" disabled={!!feedback}
                  onClick={() => { setAnswer(a => a + c); inputRef.current?.focus(); }}>{c}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={`lesson-foot${feedback ? (right ? ' good' : ' bad') : ''}`}>
        {feedback ? (
          <div className="feedback">
            <div className="feedback-text">
              <strong>
                {feedback.result === 'right' ? PRAISE[(done + total) % PRAISE.length]
                  : feedback.result === 'accent' ? 'Almost! Watch the accent:' : 'Correct answer:'}
              </strong>
              {feedback.result !== 'right' && <span className="feedback-answer">{feedback.answer}</span>}
              {q.why && <span className="feedback-why">{q.why}</span>}
              {feedback.result === 'wrong' && hearts <= 0 && <span className="small">That was your last heart.</span>}
            </div>
            <button className={`primary big ${right ? 'go' : 'stop'}`} onClick={next} autoFocus>Continue</button>
          </div>
        ) : (
          <button className="primary big check" onClick={submit} disabled={!answer.trim()}>Check</button>
        )}
      </div>
    </div>
  );
}

/** Match: right when every form has the pronoun that goes with it (two people with the same form can swap). */
function matchResult(q: Question, placed: (number | null)[]): 'right' | 'wrong' {
  const pairs = q.pairs ?? [];
  const formOf = (who: string) => pairs.find(p => p.who === who)?.form;
  return pairs.every((p, k) => placed[k] != null && formOf(q.tiles![placed[k]!]) === p.form) ? 'right' : 'wrong';
}

/**
 * Drag each pronoun onto its form: with the mouse or a finger (pointer events,
 * so phones work too), or tap a pronoun and then tap its form. Tapping a placed
 * pronoun sends it back.
 */
function MatchBoard({ q, placed, setPlaced, locked }: { q: Question; placed: (number | null)[]; setPlaced: (p: (number | null)[]) => void; locked: boolean }) {
  const pairs = q.pairs ?? [], chips = q.tiles ?? [];
  const [selected, setSelected] = useState<number | null>(null);
  const [drag, setDrag] = useState<{ chip: number; x: number; y: number } | null>(null);
  const used = new Set(placed.filter((c): c is number => c != null));
  const put = (chip: number, slot: number) => {
    const next = pairs.map((_, k) => (placed[k] === chip ? null : placed[k] ?? null));
    next[slot] = chip;
    setPlaced(next);
    setSelected(null);
  };
  const takeBack = (slot: number) => { if (locked) return; const next = pairs.map((_, k) => placed[k] ?? null); next[slot] = null; setPlaced(next); };

  const down = (chip: number) => (e: React.PointerEvent) => {
    if (locked) return;
    e.preventDefault();
    const x0 = e.clientX, y0 = e.clientY;
    let moved = false;
    const move = (ev: PointerEvent) => {
      if (!moved && Math.hypot(ev.clientX - x0, ev.clientY - y0) > 6) moved = true;
      if (moved) setDrag({ chip, x: ev.clientX, y: ev.clientY });
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up);
      setDrag(null);
      if (!moved && Math.hypot(ev.clientX - x0, ev.clientY - y0) > 6) moved = true;   // a quick flick
      if (!moved) { setSelected(s => (s === chip ? null : chip)); return; }
      const slot = (document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null)?.closest<HTMLElement>('[data-slot]')?.dataset.slot;
      if (slot != null) put(chip, Number(slot));
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); window.addEventListener('pointercancel', up);
  };

  const formOf = (who: string) => pairs.find(p => p.who === who)?.form;
  return (
    <div className="match-board">
      <div className="match-chips" aria-label="Pronouns">
        {chips.map((w, i) => (used.has(i) ? null : (
          <button key={i} type="button" className={`tile match-chip${selected === i ? ' picked' : ''}${drag?.chip === i ? ' dragging' : ''}`}
            disabled={locked} onPointerDown={down(i)} aria-pressed={selected === i}>{w}</button>
        )))}
        {used.size === chips.length && !locked && <span className="small muted">All placed. Check it!</span>}
      </div>
      <div className="match-rows">
        {pairs.map((p, k) => {
          const chip = placed[k];
          const ok = locked && chip != null && formOf(chips[chip]) === p.form;
          return (
            <div key={k} data-slot={k} className={`match-row${locked ? (ok ? ' good' : ' bad') : ''}${selected != null && chip == null ? ' ready' : ''}`}
              onClick={() => { if (!locked && selected != null) put(selected, k); }}>
              <span className="match-drop">
                {chip != null
                  ? <button type="button" className="tile" disabled={locked} onClick={e => { e.stopPropagation(); takeBack(k); }}>{chips[chip]}</button>
                  : <span className="match-empty">{selected != null ? 'Tap to put it here' : 'Drop a pronoun here'}</span>}
              </span>
              <span className="match-form" dir="auto">{p.form}</span>
              {locked && !ok && <span className="match-fix small">{p.who}</span>}
            </div>
          );
        })}
      </div>
      {drag && <div className="tile match-ghost" style={{ left: drag.x, top: drag.y }} aria-hidden>{chips[drag.chip]}</div>}
    </div>
  );
}
